import { useEffect, useRef } from 'react'

/**
 * How to write the command modifier for the current platform, so a shortcut hint
 * shown to the user matches the key `useHotkey` expects them to press.
 */
export const commandModifierLabel = (): string =>
  navigator.userAgent.includes('Mac') ? '⌘' : 'Ctrl'

/**
 * Runs `handler` when `key` is pressed with the command modifier held — Cmd on
 * macOS, Ctrl elsewhere.
 *
 * Both modifiers are accepted rather than sniffing the platform, so an external
 * keyboard on either OS works. The listener sits on the document, so the shortcut
 * fires regardless of what currently holds focus.
 */
export const useHotkey = (key: string, handler: () => void): void => {
  const handlerRef = useRef(handler)

  // Track the newest handler in a ref so a caller re-rendering with a fresh
  // closure doesn't detach and reattach the listener.
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) {
        return
      }
      // AltGr reports as Ctrl+Alt on Windows/Linux, where the combination can
      // produce a character rather than invoke a shortcut.
      if (event.altKey) {
        return
      }
      if (event.key.toLowerCase() !== key.toLowerCase()) {
        return
      }
      // Some browsers bind these combinations themselves — Firefox opens its own
      // search bar on Ctrl+K.
      event.preventDefault()
      handlerRef.current()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [key])
}
